import { createContext, useContext, useEffect, useState } from 'react';
import { useFeatureIsOn } from '@monofront/business/contexts';
import { FEATURE_FLAG_GOOGLE_SHOPPING_ENABLED } from '@monofront/business/constants';
import { useAuthentication } from '@context/businessContainer';
import { getQueryParam } from '@utils/query';

const SOURCE_KEY = 'funnel';
const SOURCE_VALUE = 'shopping';

interface IGoogleShoppingContextProps {
  isFromGoogleShopping: boolean | undefined;
}

const GoogleShoppingContext = createContext<IGoogleShoppingContextProps>({
  isFromGoogleShopping: undefined,
});

/**
 * Context that sets the Google Shopping source in storage and
 * indicates if Google Shopping is supported (according to a growthbook flag) AND active
 * @returns GoogleShoppingProvider, useGoogleShoppingContext
 */
const GoogleShoppingProvider: React.FC<{ children?: React.ReactNode }> = ({
  children,
}) => {
  const { isMember, isLoading } = useAuthentication();
  const [isFromGoogleShopping, setIsFromGoogleShopping] = useState<
    boolean | undefined
  >(undefined);
  const isGoogleShoppingEnabled = useFeatureIsOn(
    FEATURE_FLAG_GOOGLE_SHOPPING_ENABLED,
  );

  useEffect(() => {
    if (!isLoading && !isFromGoogleShopping) {
      const sessionValue = JSON.parse(
        sessionStorage.getItem('fromGoogleShopping') || 'false',
      );

      if (sessionValue) {
        setIsFromGoogleShopping(true);
      } else {
        const source = getQueryParam(SOURCE_KEY) === SOURCE_VALUE;

        if (source && !isMember) {
          setIsFromGoogleShopping(true);
          sessionStorage.setItem('fromGoogleShopping', 'true');
        } else {
          setIsFromGoogleShopping(false);
        }
      }
    }
  }, [isMember, isLoading]);

  return (
    <GoogleShoppingContext.Provider
      value={{
        isFromGoogleShopping: isGoogleShoppingEnabled
          ? isFromGoogleShopping
          : false,
      }}
    >
      {children}
    </GoogleShoppingContext.Provider>
  );
};

const useGoogleShoppingContext = () => useContext(GoogleShoppingContext);

export { GoogleShoppingProvider, useGoogleShoppingContext };
